import React, { memo, useRef, useEffect, useState, useCallback } from "react";
import { dates, formats, validators, arrays } from "investira.sdk";
import { Stack, Card, Icon } from "investira.react.components";
import { StocksChart } from "investira.react.charts";
import { COTACAO_TIPO } from "@investirapri/financa.js/lib/enums";
import Controls from "./Controls";

const IndicadoresCotacao = memo((props) => {
  const { indicador, cotacoes, isPageLoading } = props;
  const chartRef = useRef(null);

  const xCotacaoTipo = arrays.arrayToObject(Object.values(COTACAO_TIPO), "id");

  function formatData(pData = [], pAjustado = true, pType = 0) {
    if (validators.isNull(pData)) {
      return [];
    }

    const xCotacao = "rentabilidade";
    const xTypes = [xCotacao];

    const xFormattedData = pData.map((xPoint) => {
      const xDate = dates.toDate(xPoint.data).getTime();
      const xType = xTypes[pType];
      return {
        x: xDate,
        y: xPoint[xType],
        metadata: {
          ...xPoint,
        },
      };
    });

    return xFormattedData;
  }

  const createChartOptions = useCallback(
    (pCotacoes, pAjustado = true, pType = 0) => {
      const to = new Date().getTime();
      const from = new Date(to - 365 * 24 * 3600 * 1000).getTime();

      const xSeries = pCotacoes.map((pCotacao) => {
        const xData = pCotacao.data;

        return {
          name: pCotacao.indicador,
          data: formatData(xData, pAjustado, pType),
          connectEnds: true,
        };
      });

      const xOptions = {
        title: false,
        chart: {
          zooming: {
            type: "x",
          },
          events: {
            load: function () {
              const chart = this;
              chart.xAxis[0].setExtremes(from, to);
            },
          },
        },
        rangeSelector: {
          enabled: false,
          buttons: [
            {
              type: "month",
              count: 1,
              text: "1M",
            },
            {
              type: "month",
              count: 6,
              text: "6M",
            },
            {
              type: "year",
              count: 1,
              text: "1A",
            },
            {
              type: "year",
              count: 2,
              text: "2A",
            },
            {
              type: "year",
              count: 3,
              text: "3A",
            },
            {
              type: "year",
              count: 5,
              text: "5A",
            },
            {
              type: "ytd",
              text: "YTD",
            },
            {
              type: "all",
              text: "MÁX.",
            },
          ],
          selected: 2,
        },
        xAxis: { type: "datetime", crosshair: true },
        yAxis: {
          type: "linear",
          opposite: false,
          title: false,
          labels: {
            formatter: function () {
              return `${this.value}%`;
            },
          },
        },
        series: xSeries,
        tooltip: {
          crosshairs: true,
          shared: true,
          formatter: function () {
            const xDate = formats.formatDateCustom(this.point.x, "DD/MMM/YYYY");

            let xTooltipContent = `<span style="font-size: 1em"><b>${xDate}</b></span><br/>`;

            const xIndicadorTipoId =
              this.points[0].point.metadata.indicador_tipo_id;

            if (xIndicadorTipoId === "M") {
              this.points.forEach(function (point) {
                const xRentabilidade = formats.formatNumber(point.y, 2, false);
                xTooltipContent += `<span style="color:${point.color}">\u25CF</span>Variação Acumulada: <b>${xRentabilidade}%</b><br/>`;
                xTooltipContent += `<span style="color:${
                  point.color
                }">\u25CF</span>Cotação: <b>${formats.formatNumber(
                  point.point.metadata.cotacao,
                  4,
                  true,
                  true
                )}</b><br/>`;
              });

              return xTooltipContent;
            } else {
              this.points.forEach(function (point) {
                const xRentabilidade = formats.formatNumber(point.y, 2, false);
                xTooltipContent += `<span style="color:${point.color}">\u25CF</span>Variação Acumulada: <b>${xRentabilidade}%</b><br/>`;
                xTooltipContent += point.point.metadata.cotacao
                  ? `<span style="color:${point.color}">\u25CF</span>Cotação: <b>${point.point.metadata.cotacao}</b><br/>`
                  : "";
                xTooltipContent += `<span style="color:${
                  point.color
                }">\u25CF</span>Taxa: <b>${
                  point.point.metadata.taxa || "0"
                }</b><br/>`;
                xTooltipContent += `<span style="color:${
                  point.color
                }">\u25CF</span>Data Divulgação: <b>${formats.formatDateCustom(
                  point.point.metadata.data,
                  "DD/MMM/YYYY"
                )}</b><br/>`;
                //xTooltipContent += `<hr/>`
                xTooltipContent += `<span style="color:${
                  point.color
                }">\u25CF</span>Cotação Tipo:  <b>${
                  xCotacaoTipo[point.point.metadata.cotacao_tipo_id].descricao
                }</b><br/>`;
              });

              return xTooltipContent;
            }
          },
        },
      };

      return xOptions;
    },
    []
  );

  const [ajustado, setAjustado] = useState(false);
  const [type, setType] = useState(0);
  const [options, setOptions] = useState(null);

  const handleRangeSelect = (type, count) => {
    const xChart = chartRef.current;
    const xTo = new Date().getTime();
    let xFrom;

    switch (type) {
      case "month":
        xFrom = new Date(xTo - count * 30 * 24 * 3600 * 1000).getTime();
        break;
      case "year":
        xFrom = new Date(xTo - count * 365 * 24 * 3600 * 1000).getTime();
        break;
      case "ytd":
        const startOfYear = new Date(new Date().getFullYear(), 0, 1).getTime();
        xFrom = startOfYear;
        break;
      case "all":
        xFrom = null;
        break;
      default:
        return;
    }

    xChart.xAxis[0].setExtremes(xFrom, xTo);
  };

  useEffect(() => {
    const xOptions = createChartOptions(cotacoes, ajustado, type);
    setOptions(xOptions);
  }, [cotacoes, ajustado, type, createChartOptions]);

  if (validators.isEmpty(cotacoes[0].data)) {
    return null;
  }

  if (validators.isNull(options)) {
    return null;
  }

  return (
    <Stack>
      <Card>
        <Stack>
          <Stack
            flexGrow={1}
            py={2}
            px={2}
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Stack>
              <Icon iconName="chart_line" color="primary" size={21} />
            </Stack>
            <Stack flexGrow="1"></Stack>
            <Controls
              range={{
                selected: options.rangeSelector.selected,
                buttons: options.rangeSelector.buttons,
                setRange: handleRangeSelect,
              }}
              ajustado={{
                onChange: setAjustado,
              }}
              type={{
                value: type,
                onChange: setType,
              }}
            />
          </Stack>
          <Stack sx={{ px: 2, py: 2 }}>
            <StocksChart key={"cotacao"} ref={chartRef} options={options} />
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
});

IndicadoresCotacao.displayName = "IndicadoresCotacao";

export default IndicadoresCotacao;
